import { useCallback, useEffect, useState } from 'react';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import { useLayoutContext } from 'context/LayoutContext';
import { useUpdateCaptableCustomBreakpoints } from 'services/hooks/captable';
import useWaterfallBreakpointsTableData from './useWaterfallBreakpointsTableData';
import { CustomBreakpointsData, WaterfallBreakpointsProps } from '../../types/breakpoint-analysis.models';

const WaterfallBreakpoints = ({
  waterfallColumns,
  format,
  hideGrid,
  initialLoadWithCustomBPs,
  isDisabled,
  isAllocationFinal,
  capTableId,
  customBreakpointsGridRef,
}: WaterfallBreakpointsProps) => {
  const { spreadsheet: WaterfallBreakpointsDetailSheet } = useWaterfallBreakpointsTableData({
    waterfallColumns,
    format,
  });
  const { updatePageActions } = useLayoutContext();

  const [spreadsheets, setSpreadsheets] = useState([WaterfallBreakpointsDetailSheet]);
  const { updateCustomBreakpoints } = useUpdateCaptableCustomBreakpoints();

  useEffect(() => {
    if (WaterfallBreakpointsDetailSheet?.yLength > 0) {
      setSpreadsheets([WaterfallBreakpointsDetailSheet]);
    }
  }, [WaterfallBreakpointsDetailSheet]);

  const { onChange, workbook } = useWorkbook(spreadsheets);

  const WaterfallBreakpointsDetailSheetProps = {
    ...WaterfallBreakpointsDetailSheet,
    sheet: WaterfallBreakpointsDetailSheet!,
    workbook,
    onChange,
    page: 'captable',
    className: 'waterfall-custom-width',
    data: WaterfallBreakpointsDetailSheet?.data ?? [],
  };

  const handleSaveWaterfallBreakpoints = useCallback(async () => {
    await updateCustomBreakpoints(capTableId, {
      ...customBreakpointsGridRef.current,
      use_custom_breakpoints: false,
    } as CustomBreakpointsData);
    // change flag to the save button goes away after saving
    // eslint-disable-next-line no-param-reassign
    initialLoadWithCustomBPs.current = false;
  }, [capTableId, updateCustomBreakpoints, initialLoadWithCustomBPs, customBreakpointsGridRef]);

  // if initial load is with custom BPs and we're showing this,
  // means we should display the "Save" button to confirm new choice for waterfall BPs
  useEffect(() => {
    if (initialLoadWithCustomBPs.current && updatePageActions) {
      updatePageActions({
        mainAction: {
          id: 'persist-waterfall-breakpoints',
          label: 'Save',
          isActive: isDisabled,
          callback: handleSaveWaterfallBreakpoints,
          isLock: isAllocationFinal,
        },
      });
    } else if (updatePageActions && !initialLoadWithCustomBPs.current) {
      // this will kick in when the save button was clicked and we need to remove it because there's no point in
      // re-saving again waterfall BPs if nothing changed
      updatePageActions(null);
    }
  }, [handleSaveWaterfallBreakpoints, initialLoadWithCustomBPs, isAllocationFinal, isDisabled, updatePageActions]);

  if (hideGrid) {
    return null;
  }

  return WaterfallBreakpointsDetailSheet?.data ? (
    <ScalarSpreadsheet {...WaterfallBreakpointsDetailSheetProps} page={'captable'} />
  ) : (
    <GridSkeleton />
  );
};

export default WaterfallBreakpoints;
